interface WorkOrder {
  id: string;
  title: string;
  description: string;
  status: string;
  priority: 'critical' | 'high' | 'medium' | 'low';
  assignee: string;
  asset: string;
  dueDate: string;
  createdDate: string;
  estimatedHours: number;
  completedHours?: number;
}

interface WorkOrderCardProps {
  order: WorkOrder;
}

export function WorkOrderCard({ order }: WorkOrderCardProps) {
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'open': return 'ri-time-line';
      case 'in-progress': return 'ri-play-circle-line';
      case 'completed': return 'ri-check-circle-line';
      case 'on-hold': return 'ri-pause-circle-line';
      default: return 'ri-circle-line';
    }
  };

  const isOverdue = new Date(order.dueDate) < new Date() && order.status !== 'completed';

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h4 className="font-semibold text-gray-900 mb-1 line-clamp-2">{order.title}</h4>
          <p className="text-sm text-gray-600 mb-2">#{order.id}</p>
        </div>
        <div className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(order.priority)}`}>
          {order.priority.charAt(0).toUpperCase() + order.priority.slice(1)}
        </div>
      </div>

      <p className="text-sm text-gray-600 mb-3 line-clamp-2">{order.description}</p>

      <div className="space-y-2 mb-3">
        <div className="flex items-center text-sm text-gray-600">
          <i className="ri-tools-line mr-2"></i>
          <span>{order.asset}</span>
        </div>
        
        <div className="flex items-center text-sm text-gray-600">
          <i className="ri-user-line mr-2"></i>
          <span>{order.assignee}</span>
        </div>

        <div className={`flex items-center text-sm ${isOverdue ? 'text-red-600' : 'text-gray-600'}`}>
          <i className="ri-calendar-line mr-2"></i>
          <span>Due: {new Date(order.dueDate).toLocaleDateString()}</span>
          {isOverdue && <i className="ri-error-warning-line ml-1 text-red-500"></i>}
        </div>
      </div>

      {order.status === 'in-progress' && order.completedHours && (
        <div className="mb-3">
          <div className="flex justify-between text-sm text-gray-600 mb-1">
            <span>Progress</span>
            <span>{order.completedHours}h / {order.estimatedHours}h</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full" 
              style={{ width: `${Math.min((order.completedHours / order.estimatedHours) * 100, 100)}%` }}
            ></div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center text-sm text-gray-500">
          <i className={`${getStatusIcon(order.status)} mr-1`}></i>
          <span className="capitalize">{order.status.replace('-', ' ')}</span>
        </div>
        
        <div className="flex space-x-2">
          <button className="p-1 text-gray-400 hover:text-blue-600">
            <i className="ri-edit-line"></i>
          </button>
          <button className="p-1 text-gray-400 hover:text-gray-600">
            <i className="ri-more-line"></i>
          </button>
        </div>
      </div>
    </div>
  );
}